import React, { useState } from 'react';
import { Card, Btn, Badge, Modal, FormRow, EmptyState } from '../components/UI';
import { format, parseISO } from 'date-fns';

const TAGS = ['General', 'Observation', 'Harvest', 'Weather', 'Pest & disease', 'Planning', 'Visitor', 'Recipe idea'];

function tagColor(tag) {
  if (tag === 'Harvest') return 'green';
  if (tag === 'Pest & disease') return 'red';
  if (tag === 'Weather') return 'stone';
  if (tag === 'Planning') return 'soil';
  if (tag === 'Recipe idea') return 'amber';
  return 'stone';
}

function AddEntryModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    title: '',
    body: '',
    tag: 'General',
    date: format(new Date(), 'yyyy-MM-dd'),
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleSave() {
    if (!form.body.trim()) return;
    onSave({
      id: 'j_' + Date.now(),
      ...form,
      createdAt: new Date().toISOString(),
    });
  }

  return (
    <Modal title="New journal entry" onClose={onClose} width={560}>
      <FormRow label="Title (optional)">
        <input value={form.title} onChange={e => set('title', e.target.value)}
          placeholder="e.g. First courgette of the season" autoFocus />
      </FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Date">
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        </FormRow>
        <FormRow label="Tag">
          <select value={form.tag} onChange={e => set('tag', e.target.value)}>
            {TAGS.map(t => <option key={t}>{t}</option>)}
          </select>
        </FormRow>
      </div>
      <FormRow label="Entry">
        <textarea value={form.body} onChange={e => set('body', e.target.value)}
          placeholder="What happened in the garden today?" style={{ minHeight: 160 }} />
      </FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={!form.body.trim()}>Save entry</Btn>
      </div>
    </Modal>
  );
}

function JournalEntry({ entry, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const preview = entry.body.length > 200 && !expanded;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '1.25rem',
      marginBottom: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {entry.date ? format(parseISO(entry.date), 'EEEE d MMMM yyyy') : ''}
            </span>
            <Badge color={tagColor(entry.tag)}>{entry.tag}</Badge>
          </div>
          {entry.title && (
            <h3 style={{ fontSize: 16, fontFamily: 'var(--font-display)', fontWeight: 500 }}>{entry.title}</h3>
          )}
        </div>
        <button onClick={() => onDelete(entry.id)}
          style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>✕</button>
      </div>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {preview ? entry.body.slice(0, 200) + '…' : entry.body}
      </p>
      {entry.body.length > 200 && (
        <button onClick={() => setExpanded(e => !e)}
          style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 6, padding: 0 }}>
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

export default function Journal({ state, dispatch }) {
  const [addModal, setAddModal] = useState(false);
  const [filterTag, setFilterTag] = useState('All');
  const { journalEntries = [] } = state;

  const sorted = [...journalEntries].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const filtered = filterTag === 'All' ? sorted : sorted.filter(e => e.tag === filterTag);

  const usedTags = ['All', ...new Set(journalEntries.map(e => e.tag).filter(Boolean))];

  return (
    <div style={{ padding: '2rem', maxWidth: 700 }} className="fade-up">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 4 }}>Journal</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{journalEntries.length} entr{journalEntries.length !== 1 ? 'ies' : 'y'}</p>
        </div>
        <Btn variant="primary" onClick={() => setAddModal(true)}>+ New entry</Btn>
      </div>

      {usedTags.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          {usedTags.map(t => (
            <button key={t} onClick={() => setFilterTag(t)} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 20, border: '1px solid', cursor: 'pointer',
              borderColor: filterTag === t ? 'var(--accent)' : 'var(--border-md)',
              background: filterTag === t ? 'var(--accent-light)' : 'transparent',
              color: filterTag === t ? 'var(--accent)' : 'var(--text-secondary)',
            }}>{t}</button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon="◫" title="No entries yet"
          body="Keep a record of what's happening in the garden — harvests, observations, ideas for the menu."
          action={<Btn variant="primary" onClick={() => setAddModal(true)}>Write first entry</Btn>} />
      ) : (
        filtered.map(entry => (
          <JournalEntry key={entry.id} entry={entry}
            onDelete={id => dispatch({ type: 'DELETE_JOURNAL', id })} />
        ))
      )}

      {addModal && (
        <AddEntryModal
          onSave={entry => { dispatch({ type: 'ADD_JOURNAL', entry }); setAddModal(false); }}
          onClose={() => setAddModal(false)}
        />
      )}
    </div>
  );
}
